import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ModernCta } from '../../../../widgets/modern-cta/modern-cta';
import { AgenticBotDemo } from './agentic-bot-demo/agentic-bot-demo';

interface KeyFeature {
  badge: string;
  icon: 'command' | 'sparkle' | 'flower' | 'clock';
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  imagePosition: 'left' | 'right';
}

interface NumberedItem {
  number: string;
  title: string;
  link?: string;
}

@Component({
  selector: 'app-agentic-bot',
  standalone: true,
  imports: [RouterLink, ModernCta, AgenticBotDemo],
  templateUrl: './agentic-bot.html',
  styleUrl: './agentic-bot.scss',
})
export class AgenticBot {
  readonly workflowTicker = [
    'ITSM Workflows',
    'Autonomous Actions',
    'Knowledge-Led Answers',
    'Enterprise Service',
  ];

  readonly keyFeatures: KeyFeature[] = [
    {
      badge: '01',
      icon: 'command',
      title: 'Tool-Driven Agent Framework',
      description:
        'Built using MCP and LangGraph, our Agentic Bots operate through tools rather than prompts alone. The framework gives service agents the ability to take actions inside incident management workflows, reducing manual intervention in routine tasks.',
      image: '/assets/img/feature-imgs/itsm/agentic-bot/tool-driven-agent.avif',
      imageAlt: 'Agentic Bot incident triage tool execution',
      imagePosition: 'right',
    },
    {
      badge: '02',
      icon: 'sparkle',
      title: 'Configurable Bot Assistants',
      description:
        'Your teams can build their own custom bots through a no-code/low-code interface. They can set the scope using documents, knowledge base, policies, SOPs, or manuals. Each bot can support different teams and use cases with less upkeep.',
      image: '/assets/img/feature-imgs/itsm/agentic-bot/configurable-bot.avif',
      imageAlt: 'Agentic Bot builder configuration screen',
      imagePosition: 'left',
    },
    {
      badge: '03',
      icon: 'flower',
      title: 'Smart Knowledge Responses',
      description:
        'Our Agentic Bots answer within selected knowledge sources, helping reduce hallucinations and keep responses aligned with approved information. The framework supports 24/7 self-service and a 40-60% deflection rate for routine questions.',
      image: '/assets/img/feature-imgs/itsm/agentic-bot/smart-knowledge.avif',
      imageAlt: 'Agentic Bot knowledge response with sources',
      imagePosition: 'right',
    },
    {
      badge: '04',
      icon: 'clock',
      title: '24/7 Self-Service',
      description:
        'Our Agentic Bots give users 24/7 self-service for routine queries through defined knowledge sources. With a 40-60% deflection rate for routine requests, teams can reduce repetitive service interactions while providing enterprise-grade virtual agents for varied use cases.',
      image: '/assets/img/feature-imgs/itsm/agentic-bot/self-service.avif',
      imageAlt: 'Self-service portal deflection metrics',
      imagePosition: 'left',
    },
  ];

  readonly otherItsmFeatures: NumberedItem[] = [
    {
      number: '01',
      title: 'Incident Management',
      link: '/itsm-tool/features/incident-management-software',
    },
    {
      number: '02',
      title: 'Change Management',
      link: '/itsm-tool/features/change-management-software',
    },
    {
      number: '03',
      title: 'Service Request Management',
      link: '/itsm-tool/features/service-request-management',
    },
    { number: '04', title: 'Workflow Automation', link: '/itsm-tool/features/workflow-automation' },
    { number: '05', title: 'Event Management', link: '/itsm-tool/features/event-management' },
    {
      number: '06',
      title: 'Problem Management',
      link: '/itsm-tool/features/problem-management-software',
    },
    { number: '07', title: 'Self-Service Portal', link: '/itsm-tool/features/self-service-portal' },
    {
      number: '08',
      title: 'Service Catalogue Management',
      link: '/itsm-tool/features/catalog-management',
    },
  ];
}
